<template>
  <v-card 
    flat
    class="rounded-lg ma-1"
  >
    <v-card-title class="d-flex align-center">
      Usuários
      <v-btn
        class="ml-1 text-none font-weight-regular"
        icon="mdi-plus"
        variant="plain"
        size="medium"
        @click="openNewUserDialog"
      ></v-btn>
    </v-card-title>

    <v-card-text>
      <div class="d-flex justify-end py-4 px-2 ga-2">
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          density="compact"
          label="Procurar"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          hide-details
          single-line
        ></v-text-field>
      </div>
      
      <v-data-table 
        v-model:search="search" 
        :items="allUsers"
        :headers="headers"
      >
        <template v-slot:[`item.role`]="{ item }">
          {{ USER_ROLE[item.role].label }}
        </template>

        <template v-slot:[`item.sectorId`]="{ item }">
          {{ getUserSector(item) }}
        </template>

        <template v-slot:[`item.access`]="{ item }">
          <v-icon :color="item.access ? 'success' : 'error'">
            {{ item.access ? 'mdi-check-circle' : 'mdi-close-circle' }}
          </v-icon>
        </template>
        
        <template v-slot:[`item.action`]="{ item }">
          <v-btn
            v-if="item.role !== USER_ROLE.ADMIN.id && item.role !== USER_ROLE.APPROVER.id"
            class="text-none font-weight-regular"
            icon="mdi-pencil"
            variant="plain"
            size="small"
            @click="editUser(item)"
          ></v-btn>
          <v-btn
            v-if="item.role !== USER_ROLE.ADMIN.id"
            class="text-none font-weight-regular"
            :icon="item.access ? 'mdi-account-off' : 'mdi-account-check'"
            :color="item.access ? 'warning' : 'success'"
            variant="plain"
            size="small"
            @click="toggleUserStatus(item)"
          ></v-btn>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog
      v-model="userDialog"
      max-width="600"
    >
      <v-card
        :title="isEditMode ? 'Editar Usuário' : 'Novo Usuário'"
        :prepend-icon="isEditMode ? 'mdi-pencil' : 'mdi-plus'"
      >
        <v-card-text>
          <v-form ref="form" @submit.prevent="saveUser">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.firstName"
                  label="Nome"
                  required
                  :rules="[v => !!v || 'Nome é obrigatório']"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.lastName"
                  label="Sobrenome"
                  required
                  :rules="[v => !!v || 'Sobrenome é obrigatório']"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-text-field
                  v-model="form.email"
                  label="Email"
                  type="email"
                  required
                  :rules="[
                    v => !!v || 'Email é obrigatório',
                    v => /.+@.+\..+/.test(v) || 'Email deve ser válido'
                  ]"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.role"
                  label="Função"
                  :items="roleOptions"
                  item-title="label"
                  item-value="id"
                  required
                  :rules="[v => !!v || 'Função é obrigatória']"
                ></v-select>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.sectorId"
                  label="Setor"
                  :items="sectorOptions"
                  item-title="description"
                  item-value="id"
                  required
                  :rules="[v => !!v || 'Setor é obrigatório']"
                >
                  <template v-slot:prepend-item>
                    <v-list-item @mousedown.stop>
                      <FormFieldDialog
                          :type="GENERAL_OPTIONS.SECTOR.id"
                      />
                    </v-list-item>

                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-select>
              </v-col>
              
              <v-col cols="12">
                <v-switch
                  v-model="form.access"
                  label="Ativo"
                  color="success"
                  hide-details
                ></v-switch>
              </v-col>
              
              <v-col v-if="!isEditMode" cols="12" md="6">
                <v-text-field
                  v-model="form.password"
                  label="Senha"
                  type="password"
                  required
                  :rules="[v => !!v || 'Senha é obrigatória']"
                ></v-text-field>
              </v-col>
              
              <v-col v-if="!isEditMode" cols="12" md="6">
                <v-text-field
                  v-model="form.confirmPassword"
                  label="Confirmar Senha"
                  type="password"
                  required
                  :rules="[
                    v => !!v || 'Confirmação de senha é obrigatória',
                    v => v === form.password || 'As senhas não coincidem'
                  ]"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          
          <v-btn
            text="Cancelar"
            variant="plain"
            @click="userDialog = false"
          ></v-btn>
          
          <v-btn
            color="primary"
            text="Salvar"
            variant="flat"
            :disabled="!isFormValid"
            @click="saveUser"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <v-snackbar
      v-model="snackbar.active"
      :timeout="4000"
      :color="snackbar.color"
      elevation="24"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-card>
</template>

<script>
import FormFieldDialog from '@/components/form-fields/FormFieldDialog.vue';

import { mapState, mapActions, mapGetters } from 'vuex';
import { USER_ROLE, GENERAL_OPTIONS } from '@/helpers/constants/app.constants';

export default {
  name: 'UserContent',

  components: {
    FormFieldDialog
  },

  data: () => ({
    USER_ROLE,
    GENERAL_OPTIONS,
    search: '',
    userDialog: false,
    isEditMode: false,
    selectedUser: null,
    form: {
      id: null,
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      sectorId: '',
      active: true,
      password: '',
      confirmPassword: ''
    },
    snackbar: {
      text: '',
      color: '',
      active: false
    }
  }),
  
  computed: {
    ...mapState('user', ['allUsers']),
    ...mapGetters('general', ['sectors']),

    sectorOptions() {
      return this.sectors.filter(sector => sector.description !== 'ADM')
    },

    roleOptions() {
      return Object.values(USER_ROLE).filter(role => role.id !== USER_ROLE.ADMIN.id && role.id !== USER_ROLE.APPROVER.id);
    },

    isFormValid() {
      if (!this.form.firstName || !this.form.lastName || !this.form.email || !this.form.role || !this.form.sectorId) {
        return false;
      }
      
      if (!this.isEditMode && (!this.form.password || !this.form.confirmPassword || this.form.password !== this.form.confirmPassword)) {
        return false;
      }
      
      return true;
    },

    headers() {
      return [
        { title: 'Nome', key: 'firstName' },
        { title: 'Sobrenome', key: 'lastName' },
        { title: 'Email', key: 'email' },
        { title: 'Função', key: 'role' },
        { title: 'Setor', key: 'sector.description' },
        { title: 'Ativo', key: 'access', align: 'start' },
        { title: 'Ações', key: 'action', sortable: false, align: 'start' }
      ]
    },
  },
  
  mounted() {
    if(this.allUsers.length === 0) {
      this.initAllUsers();
    }
  },

  methods: {
    ...mapActions('user', [
      'initAllUsers', 
      'updateAccess', 
      'updateUser',
      'createUser'
    ]),

    openNewUserDialog() {
      this.isEditMode = false;
      this.form = {
        id: null,
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        sectorId: '',
        access: true,
        password: '',
        confirmPassword: ''
      };
      this.userDialog = true;
    },
    
    editUser(item) {
      this.isEditMode = true;
      this.selectedUser = item;
      this.form = { 
        ...this.selectedUser,
        sectorId: this.selectedUser.sector.id,
        password: '',
        confirmPassword: ''
      };
      this.userDialog = true;
    },
    
    async toggleUserStatus(item) {
      const user = item;
      const updatedUser = {
        ...user,
        access: !user.access
      };
      
      try {
        const { success, error } = await this.updateAccess({ id: user.id, access: !user.access });
        
        if (success) {
          this.snackbarHandler({ 
            color: 'success', 
            text: `Usuário ${updatedUser.access ? 'ativado' : 'desativado'} com sucesso!` 
          });
        } else {
          this.snackbarHandler({ 
            color: 'error', 
            text: `Erro ao ${updatedUser.access ? 'ativar' : 'desativar'} usuário: ${error}` 
          });
        }
      } catch (error) {
        this.snackbarHandler({ color: 'error', text: `Erro: ${error.message}` });
      }
    },
    
    async saveUser() {
      if (!this.isFormValid) return;
      
      try {
        const password = this.form.confirmPassword;

        if (this.isEditMode) {
          const { success, error } = await this.updateUser({ 
            ...this.form, 
            password,
            firstName: this.form.firstName.toUpperCase(),
            lastName: this.form.lastName.toUpperCase()
          });
          
          if (success) {
            this.snackbarHandler({ color: 'success', text: 'Usuário atualizado com sucesso!' });
          } else {
            this.snackbarHandler({ color: 'error', text: `Erro ao atualizar usuário: ${error}` });
          }
        } else {
          const { success, error } = await this.createUser({ 
            ...this.form, 
            password,
            firstName: this.form.firstName.toUpperCase(),
            lastName: this.form.lastName.toUpperCase()
          });
          
          if (success) {
            this.snackbarHandler({ color: 'success', text: 'Usuário criado com sucesso!' });
          } else {
            this.snackbarHandler({ color: 'error', text: `Erro ao criar usuário: ${error}` });
          }
        }
        
        this.userDialog = false;
      } catch (error) {
        this.snackbarHandler({ color: 'error', text: `Erro: ${error.message}` });
      }
    },
    
    snackbarHandler({color, text}) {
      this.snackbar.color = color;
      this.snackbar.text = text;
      this.snackbar.active = true;
    }
  }
}
</script> 